import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  GaraazDynamicTableV2,
  GaraazDateRange,
} from "@svan-garaaz/reactsharedlib";
import {
  Box,
  Flex,
  HStack,
  Tabs,
  TabList,
  Tab,
  Button,
  Text,
  VStack,
  Badge,
  Tag,
  TagLabel,
  TagCloseButton,
} from "@chakra-ui/react";
import { PageScaffold } from "../../components/PageScaffold";
import { useLocation } from "react-router-dom";
import services from "./services";
import endpoints from "../../utils/endpoints";
import useNotification from "../../utils/useNotification";
import dayjs from "dayjs";
import { Tooltip } from "@chakra-ui/react";
import { FaRegCopy, FaRupeeSign, FaEye, FaSyncAlt } from "react-icons/fa";
import { AiFillCheckCircle } from "react-icons/ai";
import { InvoiceStatus, OrderStatus, OrderTypes, Roles } from "./enums";
import { getAuthAxios } from "../../config/axios";
import TextLabels from "../PlaceOrderTest/enums";
import globalConstant from "../../utils/constants";
import GaraazSearch from "../../components/GaraazSearch";
import {
  GaraazCollectPaymentModal,
  CollectedBy,
  PaymentMode,
  InvoiceType,
} from "@svan-garaaz/reactsharedlib";
import { InvoiceInfo } from "./orderUtils";
import { useUserDetailsContext } from "../../context/userDetailsContext";
import { PaymentCollectionDetail, InvoiceInfoCellProps } from "./types";
import { resolveAndFormatDate } from "../../utils/dateResolvers";
import AddReturnAmountModal from "./components/AddReturnAmountModal";
import FileViewerModal from "./components/FileViewerModal";

type OrderRow = {
  _id: string;
  orderNo: string;
  orderDate: string;
  customer?: {
    name?: string;
    mobileNumber?: string;
  };
  customerData?: {
    orderNo: string;
    name?: string;
    mobileNumber?: string;
    allocatedTo?: Array<{
      supplierId: string;
    }>;
    invoiceInfo?: {
      invoiceNo?: string;
      invoiceDate?: string;
      invoiceAmount?: number;
    };
  };
};

const OrderNumberCell = ({ value, row }: any) => {
  const orderNo = value ?? row?.original?.customerData?.orderNo ?? "-";
  const [isToolTipOpen, setIsToolTipOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  if (orderNo === "-") return <Text>-</Text>;

  return (
    <Box display="flex" justifyContent="center" alignItems="center">
      <Text fontSize="md">{orderNo}</Text>
      <Tooltip
        border="0.4px solid grey"
        bg="white"
        isOpen={isToolTipOpen}
        label={
          <Text color="green" display="flex" alignItems="center" fontSize="xs">
            <AiFillCheckCircle style={{ marginRight: "5px" }} /> copied !
          </Text>
        }
      >
        <Text
          onClick={(e: any) => {
            e.preventDefault();
            e.stopPropagation();
            setIsToolTipOpen(true);
            navigator.clipboard.writeText(orderNo);
            timeoutRef.current = setTimeout(
              () => setIsToolTipOpen(false),
              1000,
            );
          }}
          color="black"
          cursor="pointer"
          fontSize="xs"
          height="fit-content"
          ml={2}
          title="Copy Order Number"
        >
          <FaRegCopy />
        </Text>
      </Tooltip>
    </Box>
  );
};

const InvoiceInfoCell = ({
  invoiceInfo,
  onMarkAsPaid,
  onAddReturnAmount,
  isLoading,
  userDetails,
  showBalanceAmount = false,
}: InvoiceInfoCellProps) => {
  const isInvoiceNoMissing =
    !invoiceInfo?.invoiceNo ||
    invoiceInfo.invoiceNo === "" ||
    invoiceInfo.invoiceNo === null;
  const isInvoiceAmountInvalid =
    invoiceInfo?.invoiceAmount === undefined ||
    invoiceInfo?.invoiceAmount === null ||
    invoiceInfo?.invoiceAmount === "" ||
    isNaN(Number(invoiceInfo.invoiceAmount));

  if (!invoiceInfo || isInvoiceNoMissing || isInvoiceAmountInvalid) {
    return (
      <Text fontSize="sm" fontWeight="600">
        -
      </Text>
    );
  }

  const isPaid = invoiceInfo.invoiceStatus === InvoiceStatus.PAID;
  const isReceived = invoiceInfo.invoiceStatus === InvoiceStatus.RECEIVED;

  const canCollectPayment = !isPaid;

  return (
    <VStack spacing={1.5} flex="1">
      <Text fontSize="sm" fontWeight="600">
        {invoiceInfo.invoiceNo}
      </Text>

      <HStack spacing={2}>
        <HStack spacing={0.5}>
          <FaRupeeSign size={12} />
          <Text fontSize="sm" fontWeight="500">
            {showBalanceAmount
              ? (invoiceInfo.balanceAmount ?? invoiceInfo.invoiceAmount)
              : invoiceInfo.invoiceAmount}
          </Text>
        </HStack>

        <Badge
          fontSize="xs"
          colorScheme={isPaid ? "green" : "orange"}
          variant="subtle"
        >
          {invoiceInfo.invoiceStatus}
        </Badge>
      </HStack>

      <Text fontSize="xs" color="gray.500">
        Invoice Date: {resolveAndFormatDate(invoiceInfo.invoiceDate)}
      </Text>

      {/*onAddReturnAmount && (
        <HStack spacing={0.5}>
          <Text fontSize="xs" color="gray.600">
            Return Amount:
          </Text>
          <FaRupeeSign size={10} />
          <Text fontSize="xs" fontWeight="500">
            {invoiceInfo.returnAmount ?? 0}
          </Text>
        </HStack>
      )*/}

      <HStack spacing={2}>
        {!isPaid && !isReceived && (
          <Button
            size="xs"
            colorScheme="green"
            variant="outline"
            leftIcon={<AiFillCheckCircle />}
            isLoading={isLoading}
            onClick={(e) => {
              e.stopPropagation();
              onMarkAsPaid();
            }}
            isDisabled={!canCollectPayment}
          >
            Receive Payment
          </Button>
        )}
        {onAddReturnAmount && (
          <Button
            size="xs"
            colorScheme="blue"
            variant="outline"
            isDisabled
            onClick={(e) => {
              e.stopPropagation();
              onAddReturnAmount();
            }}
          >
            Add Return Amount
          </Button>
        )}
      </HStack>
    </VStack>
  );
};

const Index = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const [customerId, setCustomerId] = useState(searchParams.get("customerId"));
  const [customerNameOrPhone, setCustomerNameOrPhone] = useState("");
  const [customerName, setCustomerName] = useState<string>("");
  const [outstandingAmount, setOutstandingAmount] = useState<number>(0);
  const toast = useNotification();
  const today = useMemo(
    () =>
      new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .split("T")[0],
    [],
  );
  const yearDate = useMemo(() => {
    return `2025-01-01`;
  }, []);
  const [dateRange, setDateRange] = useState({
    startDate: yearDate,
    endDate: today,
  });

  const [activeTab, setActiveTab] = useState(0);
  const paymentStatus =
    activeTab === 0 ? "UNPAID" : activeTab === 1 ? "RECEIVED" : "PAID";
  const [markingOrder, setMarkingOrder] = useState<string | null>(null);
  const [tableData, setTableData] = useState<OrderRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentCollectionDetail, setPaymentCollectionDetail] =
    useState<PaymentCollectionDetail | null>(null);
  const userDetails = useUserDetailsContext();

  const authorizationAPItoken = useMemo(
    () => localStorage.getItem(globalConstant.AUTH_TOKEN) || "",
    [],
  );
  const authAxios = getAuthAxios(
    process.env.REACT_APP_BASE_URL,
    authorizationAPItoken,
  );

  const lastParamsRef = useRef<any>({});
  const [isCollectPaymentModalOpen, setIsCollectPaymentModalOpen] =
    useState(false);
  const [isAddReturnAmountModalOpen, setIsAddReturnAmountModalOpen] =
    useState(false);
  const [returnAmountOrder, setReturnAmountOrder] = useState<{
    orderId: string;
    invoiceInfo: any;
  } | null>(null);

  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<any[]>([]);
  const [selectedMainImage, setSelectedMainImage] = useState<any>({});

  const handleOpenImageModal = (proofs: any[]) => {
    if (!proofs || proofs.length === 0) return;

    const media = proofs.map((url: string) => {
      const extension = url.split(".").pop()?.toLowerCase();
      let mimeType = "application/octet-stream";

      if (["jpg", "jpeg"].includes(extension || "")) {
        mimeType = "image/jpeg";
      } else if (extension === "png") {
        mimeType = "image/png";
      } else if (extension === "pdf") {
        mimeType = "application/pdf";
      }

      return { path: url, mimeType };
    });

    setSelectedMedia(media);
    setSelectedMainImage(media[0]);
    setIsImageModalOpen(true);
  };

  const handleOpenCollectPaymentModal = (
    orderNo: string,
    invoiceInfo: InvoiceInfo,
    customerAdvancePayment: number,
    invoiceType?: InvoiceType,
  ) => {
    setMarkingOrder(orderNo);
    setPaymentCollectionDetail((prevPaymentCollectionDetail) => {
      return {
        ...(prevPaymentCollectionDetail ?? {}),
        orderNo,
        invoiceNo: invoiceInfo?.invoiceNo,
        invoiceAmount: invoiceInfo?.balanceAmount ?? invoiceInfo?.invoiceAmount,
        customerAdvancePayment,
        invoiceType,
      };
    });
    setIsCollectPaymentModalOpen(true);
  };

  const handleCloseCollectPaymentModal = useCallback(() => {
    setIsCollectPaymentModalOpen(false);
    setMarkingOrder(null);
  }, []);

  const handleOpenAddReturnAmountModal = (
    orderId: string,
    invoiceInfo: any,
  ) => {
    setReturnAmountOrder({
      orderId,
      invoiceInfo,
    });
    setIsAddReturnAmountModalOpen(true);
  };

  const handleCloseAddReturnAmountModal = useCallback(() => {
    setIsAddReturnAmountModalOpen(false);
    setReturnAmountOrder(null);
  }, []);

  async function fetchOrdersPage(params: URLSearchParams) {
    const url = `${process.env.REACT_APP_BASE_URL}${
      endpoints.getAllGaraazOrders
    }&${params.toString()}`;
    const res = await fetch(url, {
      headers: { Authorization: authorizationAPItoken },
    });
    const data = await res.json();
    const orders = Array.isArray(data?.orders) ? data.orders : [];
    const total =
      typeof data?.totalCount === "number"
        ? data.totalCount
        : data?.count ?? orders.length;
    return { orders, total };
  }

  const fetchAllOrders = useCallback(
    async ({ offset = 0, limit = 10, ...restParams }: any) => {
      try {
        setIsLoading(true);
        const pageSize = limit;
        const globalStart = offset;
        const globalEnd = offset + limit;

        // -------------------------
        // 1) Get total counts
        // -------------------------
        const baseParams = {
          paymentStatus,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        };

        // Subscription & Virtual count
        const subCountParams = new URLSearchParams({
          ...baseParams,
          offset: "0",
          limit: "1",
        });
        subCountParams.append("orderType", OrderTypes.SUBSCRIPTION);
        subCountParams.append("orderType", OrderTypes.VIRTUAL);
        subCountParams.set("customerId", customerId as string);

        const { total: totalSub } = await fetchOrdersPage(subCountParams);

        // Regular count
        const regCountParams = new URLSearchParams({
          ...baseParams,
          offset: "0",
          limit: "1",
        });
        regCountParams.set("orderStatus", OrderStatus.DELIVERED);
        regCountParams.set("customerId", customerId as string);

        const { total: totalReg } = await fetchOrdersPage(regCountParams);

        // -------------------------
        // 2) Calculate slices
        // -------------------------

        // SUBSCRIPTION SLICE
        const subStart = globalStart;
        const subEnd = Math.min(globalEnd, totalSub);

        const subOffset = subStart < totalSub ? subStart : 0;
        const subLimit =
          subStart < totalSub ? Math.max(0, subEnd - subStart) : 0;

        // REGULAR SLICE
        const regStart = Math.max(0, globalStart - totalSub);
        const regEnd = Math.max(0, globalEnd - totalSub);

        const regOffset = regStart;
        const regLimit = Math.max(0, regEnd - regStart);

        // -------------------------
        // 3) Fetch data
        // -------------------------
        let subRows: any[] = [];
        let regRows: any[] = [];

        // Fetch subscriptions & virtual orders
        if (subLimit > 0) {
          const subParams = new URLSearchParams({
            ...baseParams,
            offset: String(subOffset),
            limit: String(subLimit),
          });
          subParams.append("orderType", OrderTypes.SUBSCRIPTION);
          subParams.append("orderType", OrderTypes.VIRTUAL);
          subParams.set("customerId", customerId as string);

          Object.entries(restParams || {}).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== "")
              subParams.set(k, String(v));
          });

          const { orders } = await fetchOrdersPage(subParams);
          subRows = orders;
        }

        // Fetch regular orders
        if (regLimit > 0) {
          const regParams = new URLSearchParams({
            ...baseParams,
            offset: String(regOffset),
            limit: String(regLimit),
          });
          regParams.set("orderStatus", OrderStatus.DELIVERED);
          regParams.set("customerId", customerId as string);

          Object.entries(restParams || {}).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== "")
              regParams.set(k, String(v));
          });

          const { orders } = await fetchOrdersPage(regParams);
          regRows = orders;
        }

        // -------------------------
        // 4) Merge results
        // -------------------------
        const rows = [...subRows, ...regRows];
        const totalCombined = totalSub + totalReg;

        setTableData(rows);
        setTotalCount(totalCombined);
        setPageCount(
          pageSize > 0 ? Math.ceil(totalCombined / pageSize) : 0,
        );

        lastParamsRef.current = { offset, limit, ...restParams };
      } catch (e) {
        setTableData([]);
      } finally {
        setIsLoading(false);
      }
    },
    [
      dateRange.startDate,
      dateRange.endDate,
      paymentStatus,
      customerId,
    ],
  );

  const handleAddReturnAmount = useCallback(
    async (returnAmountToAdd: number) => {
      if (!returnAmountOrder) return;
      try {
        await services.updateReturnAmount(
          returnAmountOrder.orderId,
          (returnAmountOrder.invoiceInfo.returnAmount || 0) + returnAmountToAdd,
        );
        toast({
          description: "Return amount added successfully",
          status: "success",
        });
        if (lastParamsRef.current) {
          fetchAllOrders(lastParamsRef.current);
        }
        handleCloseAddReturnAmountModal();
      } catch (error: any) {
        toast({
          description:
            error?.response?.data?.errorMsg || "Failed to add return amount",
          status: "error",
        });
        throw error;
      }
    },
    [returnAmountOrder, toast, handleCloseAddReturnAmountModal, fetchAllOrders],
  );

  function validateReturnAmountToAdd(returnAmountToAdd: number) {
    if (returnAmountToAdd < 0) {
      return "Return amount to add must be greater than 0";
    }

    if (
      (returnAmountOrder?.invoiceInfo?.returnAmount || 0) + returnAmountToAdd >
      Number(returnAmountOrder?.invoiceInfo?.invoiceAmount)
    ) {
      return "Total return amount must be less than or equal to invoice amount";
    }

    return null;
  }

  const TableHeading = useMemo(() => {
    const columns: any[] = [
      {
        Header: "Order Number",
        accessor: "orderNo",
        isDefault: true,
        Cell: (cellProps: any) => <OrderNumberCell {...cellProps} />,
      },

      {
        Header: "Order Date",
        accessor: "orderDate",
        Cell: ({ value }: any) =>
          value ? dayjs(value).format("DD MMM YY") : "-",
        isDefault: true,
      },
      {
        Header: "Invoice Payment",
        accessor: (row: any) => row?.invoiceInfo?.payment,
        Cell: ({ value }: any) => {
          return (
            <VStack spacing={1}>
              <Text fontSize="sm">Payment Mode : {value?.mode || "-"}</Text>
              <Text fontSize="sm">
                Collected By :{" "}
                {(value?.collector?.[0]?.firstName || "-") +
                  " " +
                  (value?.collector?.[0]?.lastName || "-")}
              </Text>
              <Text fontSize="sm">
                Collection Date :{" "}
                {value?.collectedAt
                  ? dayjs(value?.collectedAt).format("DD MMM YY HH:mm")
                  : "-"}
              </Text>
              <Text fontSize="sm">Notes : {value?.notes || "-"}</Text>
              <Text fontSize="sm" display="flex" alignItems="center">
                Proofs :{" "}
                {value?.proofs?.length > 0 ? (
                  <Box
                    as="span"
                    ml={2}
                    cursor="pointer"
                    onClick={() => handleOpenImageModal(value.proofs)}
                  >
                    <FaEye color="#3182ce" />
                  </Box>
                ) : (
                  "-"
                )}
              </Text>
            </VStack>
          );
        },
        isDefault: true,
      },
      {
        Header: "Delivery Payment",
        accessor: (row: any) => row?.deliveryInfo?.payment,
        Cell: ({ value }: any) => {
          return (
            <VStack spacing={1}>
              <Text fontSize="sm">Payment Mode : {value?.mode || "-"}</Text>
              <Text fontSize="sm">
                Collected By :{" "}
                {(value?.collector?.[0]?.firstName || "-") +
                  " " +
                  (value?.collector?.[0]?.lastName || "-")}
              </Text>
              <Text fontSize="sm">
                Collection Date :{" "}
                {value?.collectedAt
                  ? dayjs(value?.collectedAt).format("DD MMM YY HH:mm")
                  : "-"}
              </Text>
              <Text fontSize="sm">Notes : {value?.notes || "-"}</Text>
              <Text fontSize="sm" display="flex" alignItems="center">
                Proofs :{" "}
                {value?.proofs?.length > 0 ? (
                  <Box
                    as="span"
                    ml={2}
                    cursor="pointer"
                    onClick={() => handleOpenImageModal(value.proofs)}
                  >
                    <FaEye color="#3182ce" />
                  </Box>
                ) : (
                  "-"
                )}
              </Text>
            </VStack>
          );
        },
        isDefault: true,
      },
      {
        Header: "Garaaz Invoice",
        accessor: (row: any) => row?.invoiceInfo,
        Cell: ({ row, value }: any) => (
          <InvoiceInfoCell
            invoiceInfo={value}
            isLoading={markingOrder === row.original.orderNo}
            onMarkAsPaid={() =>
              handleOpenCollectPaymentModal(
                row.original.orderNo,
                value,
                row.original.customer?.advancePayment || 0,
              )
            }
            onAddReturnAmount={() =>
              handleOpenAddReturnAmountModal(
                row.original._id,
                row.original.invoiceInfo,
              )
            }
            userDetails={userDetails}
            showBalanceAmount={activeTab === 0}
          />
        ),
      },
      {
        Header: "Delivery Invoice",
        accessor: (row: any) => row?.deliveryInfo,
        Cell: ({ row, value }: any) => (
          <InvoiceInfoCell
            invoiceInfo={value}
            isLoading={markingOrder === row.original.orderNo}
            onMarkAsPaid={() =>
              handleOpenCollectPaymentModal(
                row.original.orderNo,
                value,
                row.original.customer?.advancePayment || 0,
                InvoiceType.DELIVERY,
              )
            }
            userDetails={userDetails}
            showBalanceAmount={activeTab === 0}
          />
        ),
      },
      {
        Header: "Order Handler",
        accessor: (row: any) =>
          row?.orderHandler?.[0]?.firstName && row?.orderHandler?.[0]?.lastName
            ? `${row.orderHandler[0].firstName} ${row.orderHandler[0].lastName}`
            : (row?.orderHandler?.[0]?.firstName ?? "-"),
        Cell: ({ value }: any) => value ?? "-",
        isDefault: true,
      },
    ];

    return columns;
  }, [activeTab, userDetails?.userSubType]);

  useEffect(() => {
    setDateRange({
      startDate: yearDate,
      endDate: today,
    });
  }, [today, yearDate]);

  const [isRefreshingOutstanding, setIsRefreshingOutstanding] = useState(false);

  const fetchOutstanding = useCallback(async (custId: string) => {
    try {
      setIsRefreshingOutstanding(true);
      const data = await services.refreshOutstanding(custId);
      setOutstandingAmount(data?.outstandingAmount ?? 0);
    } catch {
      // keep existing value on error
    } finally {
      setIsRefreshingOutstanding(false);
    }
  }, []);

  useEffect(() => {
    if (!customerId) {
      setCustomerName("");
      setOutstandingAmount(0);
      return;
    }

    const params = new URLSearchParams({
      paymentStatus,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
    if (customerId != null && customerId !== "") {
      params.set("customerId", customerId);
    }
    const url = `${process.env.REACT_APP_BASE_URL}${
      endpoints.getAllGaraazOrders
    }&${params.toString()}`;
    fetch(url, {
      headers: {
        Authorization: authorizationAPItoken,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data?.orders) && data.orders.length > 0) {
          const firstOrder = data.orders[0];
          setCustomerName(firstOrder?.customer?.name || "");
        } else {
          setCustomerName("");
        }
      })
      .catch(() => {
        setCustomerName("");
      });
  }, [
    customerId,
    paymentStatus,
    dateRange.startDate,
    dateRange.endDate,
    authorizationAPItoken,
  ]);

  useEffect(() => {
    if (customerId && activeTab === 0) {
      fetchOutstanding(customerId);
    }
  }, [customerId, activeTab, fetchOutstanding]);

  const handleClearCustomer = () => {
    setCustomerNameOrPhone("");
    setCustomerId("");
  };

  const handleSearchChange = (value: string) => {
    setCustomerNameOrPhone(value);
  };

  const handleSelectCustomer = (customer: any) => {
    setCustomerId(customer._id);

    const params = new URLSearchParams(location.search);
    params.delete("customerId");
    navigate({ search: params.toString() }, { replace: true });
  };

  return (
    <PageScaffold
      title={activeTab === 0 ? "Outstanding Invoices" : "Paid Invoices"}
      isHeaderVisible={false}
    >
      {!customerId ? (
        <Flex minH="70vh" align="center" justify="center" px={5}>
          <Box w="100%" maxW="720px">
            <VStack spacing={6} align="stretch">
              <VStack spacing={1}>
                <Text fontSize="2xl" fontWeight="bold" color="slate.800">
                  Find a customer
                </Text>
                <Text fontSize="sm" color="slate.500" textAlign="center">
                  Search by customer name or mobile number to view invoices.
                </Text>
              </VStack>

              <Box
                bg="white"
                p={4}
                borderRadius="full"
                boxShadow="rgba(99, 99, 99, 0.15) 0px 4px 20px"
              >
                <GaraazSearch
                  placeholder={TextLabels.CUSTOMER_NAME_OR_NUMBER}
                  queryUrl={endpoints.searchXpressCustomer}
                  axios={authAxios}
                  onSelect={handleSelectCustomer}
                  onClear={handleClearCustomer}
                  onChange={handleSearchChange}
                  acceptMobileNumber
                  hideSearchIcon
                  idField="_id"
                  valueField="name"
                  value={customerNameOrPhone}
                  searchListWidth="100%"
                  aria-label="Search customers by name or phone"
                />
              </Box>

              <HStack
                spacing={3}
                justify="center"
                color="slate.500"
                fontSize="xs"
              >
                <Badge variant="subtle" colorScheme="gray">
                  Invoices
                </Badge>
                <Badge variant="subtle" colorScheme="gray">
                  Outstanding
                </Badge>
                <Badge variant="subtle" colorScheme="gray">
                  Paid
                </Badge>
                <Badge variant="subtle" colorScheme="gray">
                  Date Range
                </Badge>
              </HStack>
            </VStack>
          </Box>
        </Flex>
      ) : (
        <Box bg="#FAFAFC">
          <Box
            position="sticky"
            top="calc(env(safe-area-inset-top) + 0px)"
            zIndex={10}
            bg="#FAFAFC"
            borderBottom="1px solid"
            borderColor="blackAlpha.100"
            px={{ base: 3, md: 5 }}
            py={3}
          >
            <Flex justify="center">
              <Box w="100%" maxW="720px">
                <Box
                  bg="white"
                  p={4}
                  borderRadius="full"
                  boxShadow="rgba(99, 99, 99, 0.15) 0px 4px 20px"
                >
                  <GaraazSearch
                    placeholder={TextLabels.CUSTOMER_NAME_OR_NUMBER}
                    queryUrl={endpoints.searchXpressCustomer}
                    axios={authAxios}
                    onSelect={handleSelectCustomer}
                    onClear={handleClearCustomer}
                    onChange={handleSearchChange}
                    acceptMobileNumber
                    hideSearchIcon
                    idField="_id"
                    valueField="name"
                    value={customerNameOrPhone}
                    searchListWidth="100%"
                    aria-label="Search customers by name or phone"
                  />
                </Box>
              </Box>
            </Flex>
          </Box>

          {/* Context bar: page info + selected customer + metrics */}
          <Box px={{ base: 3, md: 5 }} pt={4} pb={0}>
            <Flex
              align={{ base: "stretch", md: "center" }}
              direction={{ base: "column", md: "row" }}
              gap={3}
              justify="space-between"
            >
              <HStack spacing={3} wrap="wrap">
                {customerName && (
                  <Tag
                    size="lg"
                    colorScheme="blue"
                    borderRadius="full"
                    variant="subtle"
                  >
                    <TagLabel>Customer: {customerName}</TagLabel>
                    <TagCloseButton
                      aria-label="Clear selected customer"
                      onClick={handleClearCustomer}
                    />
                  </Tag>
                )}
                <Tag
                  size="lg"
                  colorScheme="teal"
                  borderRadius="full"
                  variant="solid"
                >
                  <TagLabel>Outstanding: ₹ {outstandingAmount ?? 0}</TagLabel>
                </Tag>
                <Tooltip label="Refresh Outstanding">
                  <Button
                    size="sm"
                    variant="ghost"
                    borderRadius="full"
                    isLoading={isRefreshingOutstanding}
                    onClick={() => customerId && fetchOutstanding(customerId)}
                    aria-label="Refresh Outstanding"
                    minW="auto"
                    p={1}
                  >
                    <FaSyncAlt />
                  </Button>
                </Tooltip>
              </HStack>
            </Flex>
          </Box>

          <Box px={{ base: 3, md: 5 }} py={4}>
            <Flex
              gap={4}
              align="flex-start"
              direction={{ base: "column", md: "row" }}
            >
              <Box
                w={{ base: "100%", md: "20%" }}
                borderRadius="10px"
                bg="white"
                p={5}
                boxShadow="rgba(99, 99, 99, 0.12) 0px 2px 12px"
              >
                <VStack align="stretch" spacing={4}>
                  <Box>
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      color="slate.700"
                      mb={2}
                    >
                      Filter by date
                    </Text>
                    <GaraazDateRange
                      startDate={dateRange.startDate}
                      endDate={dateRange.endDate}
                      onDateChange={(startDate: string, endDate: string) => {
                        setDateRange({ startDate, endDate });
                      }}
                    />
                    <Text fontSize="xs" color="slate.500" mt={2}>
                      Tip: Narrow the range to speed up results.
                    </Text>
                  </Box>
                </VStack>
              </Box>

              <Box
                w={{ base: "100%", md: "80%" }}
                p={0}
                bg="white"
                boxShadow="rgba(99, 99, 99, 0.12) 0px 2px 12px"
                borderRadius="10px"
              >
                <Tabs
                  isFitted
                  index={activeTab}
                  onChange={setActiveTab}
                  variant="unstyled" // remove default borders
                >
                  <TabList
                    borderBottom="1px solid #E2E8F0" // light gray separator (optional)
                  >
                    <Tab
                      _selected={{
                        borderBottom: "3px solid rgb(30, 41, 59)", // professional accent
                        color: "rgb(30, 41, 59)",
                        fontWeight: "600",
                      }}
                      _hover={{
                        color: "rgb(30, 41, 59)",
                      }}
                      pb={3}
                    >
                      Outstanding
                    </Tab>

                    <Tab
                      _selected={{
                        borderBottom: "3px solid rgb(30, 41, 59)",
                        color: "rgb(30, 41, 59)",
                        fontWeight: "600",
                      }}
                      _hover={{
                        color: "rgb(30, 41, 59)",
                      }}
                      pb={3}
                    >
                      Received
                    </Tab>
                    <Tab
                      _selected={{
                        borderBottom: "3px solid rgb(30, 41, 59)",
                        color: "rgb(30, 41, 59)",
                        fontWeight: "600",
                      }}
                      _hover={{
                        color: "rgb(30, 41, 59)",
                      }}
                      pb={3}
                    >
                      Paid
                    </Tab>
                  </TabList>
                </Tabs>

                <Box
                  sx={{
                    "& thead th": {
                      backgroundColor: "rgb(30, 41, 59) !important",
                      color: "white !important",
                    },
                    "& thead tr": {
                      backgroundColor: "rgb(30, 41, 59) !important",
                    },
                  }}
                >
                  <GaraazDynamicTableV2
                    columns={TableHeading}
                    data={tableData}
                    fetchData={fetchAllOrders}
                    totalCount={totalCount}
                    controlledPageCount={pageCount}
                    loading={isLoading}
                    skipPageReset
                    customPageSize={10}
                    dataNotFoundText="No matching invoices!"
                    params={{
                      paymentStatus,
                      startDate: dateRange.startDate,
                      endDate: dateRange.endDate,
                      ...(customerId ? { customerId } : {}),
                    }}
                  />
                </Box>
              </Box>
            </Flex>
          </Box>
        </Box>
      )}

      <GaraazCollectPaymentModal
        auth={{ authToken: authorizationAPItoken }}
        isOpen={isCollectPaymentModalOpen}
        onClose={handleCloseCollectPaymentModal}
        orderDetails={paymentCollectionDetail}
        onCollectPayment={services.receivePayment}
        title="Receive Payment"
        collector={{
          id: userDetails?._id,
          firstName: userDetails?.firstName,
          lastName: userDetails?.lastName,
          collectedBy: CollectedBy.GARAAZ,
        }}
        selectPlaceholder="Select Mode of Payment"
        maxNotesLength={500}
        onPaymentSuccess={() => {
          if (lastParamsRef.current) {
            fetchAllOrders(lastParamsRef.current);
          }
          if (customerId) {
            fetchOutstanding(customerId);
          }
          setMarkingOrder(null);
        }}
        paymentModes={[
          {
            value: PaymentMode.UPI,
            label: `${PaymentMode.UPI} (preferred)`,
          },
        ]}
      />

      {returnAmountOrder && (
        <AddReturnAmountModal
          isOpen={isAddReturnAmountModalOpen}
          onClose={handleCloseAddReturnAmountModal}
          onSubmit={handleAddReturnAmount}
          returnAmountOrderInvoiceInfo={returnAmountOrder.invoiceInfo}
          validateReturnAmountToAdd={validateReturnAmountToAdd}
        />
      )}
      <FileViewerModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        mediaFiles={selectedMedia}
        mainImage={selectedMainImage}
      />
    </PageScaffold>
  );
};

export default Index;
